import React, { useRef, useState } from 'react';
import { UploadCloud, Film, Image, Clock, Plus, Trash2, FolderOpen } from 'lucide-react';
import { MediaItem, formatTime } from '../../types/editor';
import { isVideoFileSrc } from '../../lib/editorTimeline';
import GalleryMediaPicker, { GalleryPickResult } from '../GalleryMediaPicker';

interface Props {
  mediaItems: MediaItem[];
  onImportFiles: (files: FileList) => void;
  onImportFromGallery: (result: GalleryPickResult) => void;
  onAddToTimeline: (item: MediaItem) => void;
  onRemoveMedia: (id: string) => void;
}

export default function MediaPanel({
  mediaItems,
  onImportFiles,
  onImportFromGallery,
  onAddToTimeline,
  onRemoveMedia,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [galleryOpen, setGalleryOpen] = useState(false);

  const videos = mediaItems.filter(m => m.type === 'video');
  const images = mediaItems.filter(m => m.type === 'image');

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%', gap: 8,
    }}>
      <div
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: '2px dashed rgba(99,102,241,0.3)',
          borderRadius: 10, padding: '14px 12px',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
          cursor: 'pointer', transition: 'all 0.2s',
          background: 'rgba(99,102,241,0.04)',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(99,102,241,0.6)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(99,102,241,0.3)'; }}
      >
        <UploadCloud size={20} style={{ color: '#818cf8' }} />
        <span style={{ fontSize: '0.72rem', color: '#818cf8', fontWeight: 500 }}>
          Importar arquivo local
        </span>
        <span style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.3)' }}>
          Vídeos (MP4) e Imagens (JPG, PNG)
        </span>
        <input
          ref={fileInputRef}
          type="file"
          accept="video/mp4,video/webm,image/jpeg,image/png,image/webp"
          multiple
          onChange={(e) => e.target.files && onImportFiles(e.target.files)}
          style={{ display: 'none' }}
        />
      </div>

      <button
        type="button"
        onClick={() => setGalleryOpen(true)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          padding: '0.5rem', borderRadius: 8,
          border: '1px solid rgba(99,102,241,0.35)',
          background: 'rgba(99,102,241,0.12)',
          color: '#c7d2fe', fontSize: '0.72rem', fontWeight: 600,
          cursor: 'pointer', fontFamily: 'inherit',
        }}
      >
        <FolderOpen size={14} /> Galeria / Upload na org
      </button>

      <GalleryMediaPicker
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        accept="both"
        title="Anexar da galeria"
        saveUploadsToGallery
        multiple
        onSelect={(result) => {
          onImportFromGallery(result);
        }}
      />

      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}
        className="right-panel-scroll"
      >
        {mediaItems.length === 0 && (
          <div style={{
            padding: 20, textAlign: 'center', fontSize: '0.7rem',
            color: 'rgba(255,255,255,0.25)',
          }}>
            Nenhuma mídia importada
          </div>
        )}

        {videos.length > 0 && (
          <div>
            <div style={{
              fontSize: '0.6rem', fontWeight: 600, color: 'rgba(255,255,255,0.4)',
              padding: '4px 0', display: 'flex', alignItems: 'center', gap: 4,
            }}>
              <Film size={10} /> Vídeos ({videos.length})
            </div>
            {videos.map(item => (
              <MediaItemCard key={item.id} item={item} onAdd={onAddToTimeline} onRemove={onRemoveMedia} />
            ))}
          </div>
        )}

        {images.length > 0 && (
          <div>
            <div style={{
              fontSize: '0.6rem', fontWeight: 600, color: 'rgba(255,255,255,0.4)',
              padding: '4px 0', display: 'flex', alignItems: 'center', gap: 4,
            }}>
              <Image size={10} /> Imagens ({images.length})
            </div>
            {images.map(item => (
              <MediaItemCard key={item.id} item={item} onAdd={onAddToTimeline} onRemove={onRemoveMedia} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MediaItemCard({
  item, onAdd, onRemove,
}: {
  item: MediaItem;
  onAdd: (item: MediaItem) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
      background: 'rgba(255,255,255,0.03)', borderRadius: 6,
      border: '1px solid rgba(255,255,255,0.04)',
      transition: 'all 0.15s', marginBottom: 3,
    }}>
      <div style={{
        width: 48, height: 32, borderRadius: 4, overflow: 'hidden',
        background: 'rgba(0,0,0,0.4)', flexShrink: 0,
      }}>
        {item.type === 'image' || (item.thumbnailUrl && !isVideoFileSrc(item.thumbnailUrl)) ? (
          <img
            src={item.type === 'image' ? (item.thumbnailUrl || item.blobUrl) : item.thumbnailUrl}
            alt=""
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
            style={{
            width: '100%', height: '100%', objectFit: 'cover',
          }} />
        ) : null}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: '0.62rem', color: 'rgba(255,255,255,0.7)', fontWeight: 500,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {item.name}
        </div>
        {item.duration > 0 && (
          <div style={{
            fontSize: '0.55rem', color: 'rgba(255,255,255,0.3)',
            display: 'flex', alignItems: 'center', gap: 3,
          }}>
            <Clock size={8} /> {formatTime(item.duration)}
          </div>
        )}
      </div>

      <button
        onClick={() => onAdd(item)}
        style={{
          background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)',
          borderRadius: 4, width: 24, height: 22, color: '#818cf8',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}
        title="Adicionar à timeline"
      >
        <Plus size={11} />
      </button>
      <button
        onClick={() => onRemove(item.id)}
        style={{
          background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
          borderRadius: 4, width: 24, height: 22, color: '#f87171',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}
        title="Remover"
      >
        <Trash2 size={10} />
      </button>
    </div>
  );
}
